import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import ComingSoon from "./pages/ComingSoon";
import { PUBLIC_SITE_LIVE } from "./lib/siteMode";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import DeleteAccount from "./pages/DeleteAccount";
import Advertisers from "./pages/Advertisers";
import Trip from "./pages/Trip";

// Admin pages
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import AdminLogin from "./pages/admin/Login";
import AdminLayout from "./pages/admin/AdminLayout";
import Overview from "./pages/admin/Overview";
import Sponsors from "./pages/admin/Sponsors";
import Festivals from "./pages/admin/Festivals";
import FestivalPitchDeck from "./pages/admin/FestivalPitchDeck";
import Listings from "./pages/admin/Listings";
import B2GDashboard from "./pages/admin/Dashboard";
import Concierge from "./pages/admin/Concierge";
import Charter from "./pages/admin/Charter";
import Packages from "./pages/admin/Packages";
import Drivers from "./pages/admin/Drivers";
import Passengers from "./pages/admin/Passengers";
import LiveMap from "./pages/admin/LiveMap";
import RideHistory from "./pages/admin/RideHistory";
import AuditLog from "./pages/admin/AuditLog";
import Incidents from "./pages/admin/Incidents";
import Hazards from "./pages/admin/Hazards";
import RoadStatus from "./pages/admin/RoadStatus";
import ExploreDestinations from "./pages/admin/ExploreDestinations";
import IslandAlerts from "./pages/admin/IslandAlerts";
import UtilityOutages from "./pages/admin/UtilityOutages";
import Settings from "./pages/admin/Settings";
import Corridors from "./pages/admin/Corridors";
import DriverVerification from "./pages/admin/DriverVerification";
import Revenue from "./pages/admin/Revenue";
import Reports from "./pages/admin/Reports";
import FilterAnalytics from "./pages/admin/FilterAnalytics";
import Requests from "./pages/admin/Requests";
import Team from "./pages/admin/Team";

function AdminPage({ children }: { children: React.ReactNode }) {
  return (
    <AdminAuthProvider>
      <AdminLayout>{children}</AdminLayout>
    </AdminAuthProvider>
  );
}

function Router() {
  return (
    <Switch>
      {/* Public pages. Marketing routes are a placeholder until
          VITE_PUBLIC_SITE=1 (see lib/siteMode.ts); legal, support, trip
          and admin routes are always live. */}
      <Route path="/" component={PUBLIC_SITE_LIVE ? Home : ComingSoon} />
      <Route path="/advertisers" component={PUBLIC_SITE_LIVE ? Advertisers : ComingSoon} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/support" component={Support} />
      <Route path="/delete-account" component={DeleteAccount} />
      {/* Public live trip page ("Share my trip" links from the app).
          Anon-readable RPC, standalone chrome — no auth, no layout. */}
      <Route path="/trip/:token">
        {(params) => <Trip token={params.token} />}
      </Route>

      {/* Admin login (no layout wrapper) */}
      <Route path="/admin/login">
        <AdminAuthProvider>
          <AdminLogin />
        </AdminAuthProvider>
      </Route>

      {/* Admin pages (with layout) */}
      <Route path="/admin">
        <AdminPage><Overview /></AdminPage>
      </Route>
      <Route path="/admin/sponsors">
        <AdminPage><Sponsors /></AdminPage>
      </Route>
      <Route path="/admin/festivals">
        <AdminPage><Festivals /></AdminPage>
      </Route>
      {/* Pitch-deck print view — not wrapped in AdminPage so the
          chrome (sidebar, header) doesn't show up in the printable
          page. The component handles its own auth-gating via the
          underlying RPC, which checks public.is_admin(). */}
      <Route path="/admin/festivals/:slug/pitch">
        <AdminAuthProvider>
          <FestivalPitchDeck />
        </AdminAuthProvider>
      </Route>
      <Route path="/admin/listings">
        <AdminPage><Listings /></AdminPage>
      </Route>
      <Route path="/admin/dashboard">
        <AdminPage><B2GDashboard /></AdminPage>
      </Route>
      <Route path="/admin/revenue">
        <AdminPage><Revenue /></AdminPage>
      </Route>
      <Route path="/admin/reports">
        <AdminPage><Reports /></AdminPage>
      </Route>
      <Route path="/admin/filter-analytics">
        <AdminPage><FilterAnalytics /></AdminPage>
      </Route>
      <Route path="/admin/concierge">
        <AdminPage><Concierge /></AdminPage>
      </Route>
      <Route path="/admin/charter">
        <AdminPage><Charter /></AdminPage>
      </Route>
      <Route path="/admin/corridors">
        <AdminPage><Corridors /></AdminPage>
      </Route>
      <Route path="/admin/packages">
        <AdminPage><Packages /></AdminPage>
      </Route>
      <Route path="/admin/drivers">
        <AdminPage><Drivers /></AdminPage>
      </Route>
      <Route path="/admin/passengers">
        <AdminPage><Passengers /></AdminPage>
      </Route>
      <Route path="/admin/live-map">
        <AdminPage><LiveMap /></AdminPage>
      </Route>
      <Route path="/admin/ride-history">
        <AdminPage><RideHistory /></AdminPage>
      </Route>
      <Route path="/admin/verification">
        <AdminPage><DriverVerification /></AdminPage>
      </Route>
      <Route path="/admin/incidents">
        <AdminPage><Incidents /></AdminPage>
      </Route>
      <Route path="/admin/hazards">
        <AdminPage><Hazards /></AdminPage>
      </Route>
      <Route path="/admin/road-status">
        <AdminPage><RoadStatus /></AdminPage>
      </Route>
      <Route path="/admin/explore">
        <AdminPage><ExploreDestinations /></AdminPage>
      </Route>
      <Route path="/admin/alerts">
        <AdminPage><IslandAlerts /></AdminPage>
      </Route>
      <Route path="/admin/outages">
        <AdminPage><UtilityOutages /></AdminPage>
      </Route>
      <Route path="/admin/audit">
        <AdminPage><AuditLog /></AdminPage>
      </Route>
      <Route path="/admin/settings">
        <AdminPage><Settings /></AdminPage>
      </Route>
      <Route path="/admin/requests">
        <AdminPage><Requests /></AdminPage>
      </Route>
      <Route path="/admin/team">
        <AdminPage><Team /></AdminPage>
      </Route>

      {/* Fallback */}
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
